import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { ApiGldGldsListRequest, GLD } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getGldSingleQuery = (uuid: string) => ({
  queryKey: ["gld-single", uuid],
  queryFn: () => uploadApi.apiGldGldsRead({ uuid }),
});

export const useGldSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<GLD>(queryClient, ["gld-list"], uuid, getGldSingleQuery(uuid));
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getGldListQuery = (options: ApiGldGldsListRequest) => ({
  queryKey: ["gld-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGldGldsList(options),
  placeholderData: keepPreviousData,
});

export const useGldListQuery = (options: ApiGldGldsListRequest) => {
  return useQuery(getGldListQuery(options));
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateGldQueries = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["gld-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGldListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
  }, [queryClient]);
};
